import { Component, Input, Output, EventEmitter } from '@angular/core';
import { Blog } from '../../types';

@Component({
  standalone: false,
  selector: 'mkt-content-card',
  template: `
    <div class="mkt-content-card" [class.mkt-content-card--selected]="selected" (click)="select.emit(blog.ID)">
      <img *ngIf="blog.FeaturedImageURL" [src]="blog.FeaturedImageURL"
           class="mkt-content-card__thumb" alt="" />
      <div class="mkt-content-card__header">
        <h4 class="mkt-content-card__title">{{ blog.Title }}</h4>
        <mkt-status-badge [status]="blog.Status"></mkt-status-badge>
      </div>
      <p class="mkt-content-card__summary">{{ blog.Summary }}</p>
      <div class="mkt-content-card__meta">
        <span class="mkt-content-card__score-badge" *ngIf="brandVoiceScore != null"
              [class.score-green]="brandVoiceScore >= 80"
              [class.score-yellow]="brandVoiceScore >= 60 && brandVoiceScore < 80"
              [class.score-red]="brandVoiceScore < 60">
          <i class="fa-solid fa-shield-halved"></i> {{ brandVoiceScore }}
        </span>
        <span class="mkt-content-card__revisions" *ngIf="revisionCount > 0">
          <i class="fa-solid fa-clock-rotate-left"></i> {{ revisionCount }}
        </span>
        <span class="mkt-content-card__tags" *ngIf="blog.Tags">
          <i class="fa-solid fa-tags"></i> {{ blog.Tags }}
        </span>
      </div>
      <div class="mkt-content-card__feedback" *ngIf="latestFeedback">
        <i class="fa-solid fa-comment-dots"></i> {{ latestFeedback.length > 80 ? latestFeedback.slice(0, 80) + '...' : latestFeedback }}
      </div>
      <div class="mkt-content-card__actions" *ngIf="showActions" (click)="$event.stopPropagation()">
        <!-- Primary button -->
        <button class="mkt-btn mkt-btn--approve"
                *ngIf="blog.Status === 'Draft' || blog.Status === 'Ready'"
                (click)="statusChange.emit({ blogId: blog.ID, newStatus: blog.Status === 'Draft' ? 'Ready' : 'Published' })">
          <i class="fa-solid fa-check"></i>
          {{ blog.Status === 'Draft' ? 'Approve' : publishLabel }}
        </button>

        <!-- Kebab menu -->
        <div class="mkt-kebab">
          <button class="mkt-btn mkt-btn--kebab" (click)="menuOpen = !menuOpen">
            <i class="fa-solid fa-ellipsis-vertical"></i>
          </button>
          <div class="mkt-kebab__backdrop" *ngIf="menuOpen" (click)="menuOpen = false"></div>
          <div class="mkt-kebab__menu" *ngIf="menuOpen">
            <button class="mkt-kebab__item" *ngIf="blog.Status === 'Archived'"
                    (click)="menuOpen = false; statusChange.emit({ blogId: blog.ID, newStatus: 'Draft' })">
              <i class="fa-solid fa-rotate-left"></i> Unarchive
            </button>
            <button class="mkt-kebab__item" *ngIf="blog.Status !== 'Archived'"
                    (click)="menuOpen = false; statusChange.emit({ blogId: blog.ID, newStatus: 'Archived' })">
              <i class="fa-solid fa-archive"></i> Archive
            </button>
            <div class="mkt-kebab__divider"></div>
            <button class="mkt-kebab__item mkt-kebab__item--danger" (click)="menuOpen = false; deleteBlog.emit(blog.ID)">
              <i class="fa-solid fa-trash"></i> Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .mkt-content-card {
      background: var(--mj-bg-surface);
      border: 1px solid var(--mj-border-default);
      border-radius: 8px;
      padding: 16px;
      cursor: pointer;
      transition: box-shadow 0.2s ease, border-color 0.2s ease;
    }
    .mkt-content-card:hover {
      box-shadow: var(--mj-shadow-sm);
      border-color: var(--mj-brand-primary);
    }
    .mkt-content-card--selected {
      border-color: var(--mj-brand-primary);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--mj-brand-primary) 15%, transparent);
    }
    .mkt-content-card__thumb {
      width: 100%;
      height: 140px;
      object-fit: cover;
      border-radius: 6px;
      margin-bottom: 12px;
      display: block;
      background: var(--mj-bg-page);
    }
    .mkt-content-card__header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 8px;
      margin-bottom: 8px;
    }
    .mkt-content-card__title {
      margin: 0;
      font-size: 0.95rem;
      font-weight: 600;
      color: var(--mj-text-primary);
      line-height: 1.3;
    }
    .mkt-content-card__summary {
      margin: 0 0 10px;
      font-size: 0.85rem;
      color: var(--mj-text-secondary);
      line-height: 1.4;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .mkt-content-card__meta {
      font-size: 0.75rem;
      color: var(--mj-text-disabled);
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
    }
    .mkt-content-card__meta i {
      margin-right: 4px;
    }
    .mkt-content-card__score-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 8px;
      border-radius: 10px;
      font-weight: 600;
      font-size: 0.7rem;
    }
    .mkt-content-card__score-badge.score-green {
      background: var(--mj-status-success-bg);
      color: var(--mj-status-success-text);
    }
    .mkt-content-card__score-badge.score-yellow {
      background: var(--mj-status-warning-bg);
      color: var(--mj-status-warning-text);
    }
    .mkt-content-card__score-badge.score-red {
      background: var(--mj-status-error-bg);
      color: var(--mj-status-error);
    }
    .mkt-content-card__revisions {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      color: var(--mj-text-muted);
      font-weight: 600;
    }
    .mkt-content-card__feedback {
      margin-top: 6px;
      font-size: 0.75rem;
      color: var(--mj-status-warning-text);
      line-height: 1.3;
    }
    .mkt-content-card__feedback i {
      margin-right: 3px;
    }
    .mkt-content-card__actions {
      display: flex;
      gap: 6px;
      margin-top: 10px;
      padding-top: 10px;
      border-top: 1px solid var(--mj-bg-surface-hover);
      align-items: center;
    }
    .mkt-btn {
      padding: 4px 10px;
      border: none;
      border-radius: 4px;
      font-size: 0.75rem;
      font-weight: 500;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      transition: opacity 0.15s;
    }
    .mkt-btn:hover { opacity: 0.85; }
    .mkt-btn--approve {
      background: var(--mj-status-success-bg);
      color: var(--mj-status-success-text);
    }
    .mkt-btn--kebab {
      background: var(--mj-bg-surface-hover);
      color: var(--mj-text-muted);
      padding: 4px 6px;
      margin-left: auto;
    }

    /* Kebab dropdown */
    .mkt-kebab {
      position: relative;
      margin-left: auto;
    }
    .mkt-kebab__backdrop {
      position: fixed;
      inset: 0;
      z-index: 9;
    }
    .mkt-kebab__menu {
      position: absolute;
      right: 0;
      top: 100%;
      margin-top: 4px;
      background: var(--mj-bg-surface);
      border: 1px solid var(--mj-border-default);
      border-radius: 6px;
      box-shadow: var(--mj-shadow-md);
      min-width: 140px;
      z-index: 10;
      overflow: hidden;
    }
    .mkt-kebab__item {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 8px 12px;
      border: none;
      background: none;
      font-size: 0.8rem;
      color: var(--mj-text-primary);
      cursor: pointer;
      text-align: left;
    }
    .mkt-kebab__item:hover {
      background: var(--mj-bg-page);
    }
    .mkt-kebab__item--danger {
      color: var(--mj-status-error);
    }
    .mkt-kebab__item--danger:hover {
      background: var(--mj-status-error-bg);
    }
    .mkt-kebab__divider {
      height: 1px;
      background: var(--mj-border-default);
      margin: 2px 0;
    }
  `]
})
export class ContentCardComponent {
  @Input() blog!: Blog;
  @Input() selected: boolean = false;
  /**
   * Label for the Ready-state button. Defaults to what the button actually does in both
   * hosts that render this card today — run-detail and review-queue — which mark the blog
   * Published in CDP and nothing else. A host that really routes through the Publish To
   * CMS action passes 'Push to HubSpot' instead; none of this card's hosts do yet, so the
   * default is the only label it currently shows.
   */
  @Input() publishLabel: string = 'Mark Published';
  @Input() showActions: boolean = true;
  @Input() revisionCount: number = 0;
  @Input() latestFeedback: string = '';
  @Input() brandVoiceScore: number | null = null;
  @Output() select = new EventEmitter<string>();
  @Output() statusChange = new EventEmitter<{ blogId: string; newStatus: string }>();
  @Output() deleteBlog = new EventEmitter<string>();

  menuOpen: boolean = false;
}
